/**
 * Opaque `.chordbaker` container format.
 *
 * SPEC (keep in sync with react-native/src/utils/chordbakerContainer.ts):
 * - A `.chordbaker` file is a ZIP archive (DEFLATE, level 6) containing a
 *   single entry `song.json` holding the V3 chord sheet JSON.
 * - Detection is byte-based, never extension-based: files starting with the
 *   ZIP magic `PK\x03\x04` are containers; anything else is treated as
 *   legacy plain-JSON text (pre-container exports, buffet downloads).
 * - Decoders fall back to the first non-directory `*.json` entry so files
 *   re-zipped by hand still import.
 *
 * jszip is loaded via dynamic import so Vite splits it out of the initial
 * bundle — the cost is only paid on an actual import/export action.
 */
export const CONTAINER_ENTRY_NAME = 'song.json';

/** ZIP magic bytes PK\x03\x04. */
const ZIP_MAGIC = [0x50, 0x4b, 0x03, 0x04];

/** Whether raw file bytes are a ZIP container. */
export const isContainerBytes = (bytes: ArrayBuffer | Uint8Array): boolean => {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  return ZIP_MAGIC.every((byte, i) => view[i] === byte);
};

/** Wrap chord sheet JSON in the opaque container Blob, ready for download. */
export const encodeContainer = async (json: string): Promise<Blob> => {
  const { default: JSZip } = await import('jszip');
  const zip = new JSZip();
  zip.file(CONTAINER_ENTRY_NAME, json);
  const bytes = await zip.generateAsync({
    type: 'uint8array',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });
  return new Blob([bytes], { type: 'application/octet-stream' });
};

/**
 * Extract the chord sheet JSON from container bytes.
 * @throws {Error} If the archive is unreadable or has no JSON entry.
 */
export const decodeContainer = async (bytes: ArrayBuffer): Promise<string> => {
  const { default: JSZip } = await import('jszip');

  let zip: InstanceType<typeof JSZip>;
  try {
    zip = await JSZip.loadAsync(bytes);
  } catch (error) {
    throw new Error(
      `Corrupt .chordbaker container: ${error instanceof Error ? error.message : 'unreadable archive'}`
    );
  }

  const entry =
    zip.file(CONTAINER_ENTRY_NAME) ??
    Object.values(zip.files).find(
      f => !f.dir && f.name.toLowerCase().endsWith('.json')
    );

  if (!entry) {
    throw new Error(
      `Invalid .chordbaker container: no ${CONTAINER_ENTRY_NAME} entry found`
    );
  }

  return entry.async('string');
};

/**
 * Blob.arrayBuffer() with a FileReader fallback for environments that lack
 * it (older browsers, jsdom in tests).
 */
export const blobToArrayBuffer = (blob: Blob): Promise<ArrayBuffer> => {
  if (typeof blob.arrayBuffer === 'function') {
    return blob.arrayBuffer();
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
};

/**
 * Read a song file, transparently handling both the opaque container format
 * and legacy plain-JSON files. Always returns JSON text.
 */
export const readSongFileText = async (file: File): Promise<string> => {
  const bytes = await blobToArrayBuffer(file);
  if (isContainerBytes(bytes)) {
    return decodeContainer(bytes);
  }
  return new TextDecoder().decode(bytes);
};
